import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getProjects, getKPIs } from '../../services/api';
import { logger } from '../../utils/logger';
import i18n from '../../i18n/config.js';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import ProjectsTabs from './ProjectsTabs';
import ProjectsList from './ProjectsList';
import ChatDrawer from '../ChatDrawer/ChatDrawer';
import { useConversation } from '../../hooks/useConversation';
import { pathWithPreservedParams } from '../../routing/navigation.js';
import { readAgentOpen, readProjects, writeAgentOpen, writeProjects } from '../../utils/workspaceState';
import './ProjectsView.css';

/**
 * ProjectsView component - main container for projects page
 * Uses shared conversation state (no automatic opening message).
 */
const ProjectsView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeCompany, user } = useAuth();
  const userId = user?.id;
  const companyId = activeCompany?.id;
  const [initialProjects] = useState(() => readProjects(userId, companyId));
  const [projects, setProjects] = useState({ active: [], inactive: [], no_activity: [], closed: [], internal: [] });
  const [activeTab, setActiveTab] = useState(initialProjects.activeTab);
  const [sortColumn, setSortColumn] = useState(initialProjects.sortColumn);
  const [sortDirection, setSortDirection] = useState(initialProjects.sortDirection);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(() => readAgentOpen(userId));
  const [hydratedFor, setHydratedFor] = useState(
    userId && companyId ? `${userId}:${companyId}` : null
  );
  const [kpisData, setKpisData] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState(null);

  const conversation = useConversation();

  // Get locale for formatting
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  // Format currency for display
  const formatCurrency = useCallback((value) => {
    if (value === null || value === undefined) return t('cashBalance.notAvailable');
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }, [t, locale]);

  // Format percentage for display
  const formatPercentage = useCallback((value) => {
    if (value === null || value === undefined) return t('cashBalance.notAvailable');
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  }, [t, locale]);

  // Fetch projects on mount and when company changes
  useEffect(() => {
    if (!activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchProjects = async () => {
      setLoading(true);
      setError(null);

      try {
        logger.info(`Fetching projects for company ${activeCompany.id}`);
        const response = await getProjects(activeCompany.id, signal);

        if (signal.aborted) return;
        if (response && response.active && response.inactive && response.no_activity && response.closed) {
          setProjects({
            active: response.active || [],
            inactive: response.inactive || [],
            no_activity: response.no_activity || [],
            closed: response.closed || [],
            internal: response.internal || []
          });
          logger.info(
            `Loaded ${response.active.length} active, ${response.inactive.length} inactive, ` +
            `${response.no_activity.length} no_activity, ${response.closed.length} closed, ` +
            `${(response.internal || []).length} internal projects`
          );
        } else {
          setProjects({ active: [], inactive: [], no_activity: [], closed: [], internal: [] });
          logger.warn('Unexpected response format from getProjects');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching projects:', err);
        setError(err.message || t('projects.error'));
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchProjects();
    return () => ac.abort();
  }, [activeCompany?.id, t]);

  // Fetch KPIs data on mount and when company changes
  useEffect(() => {
    if (!activeCompany?.id) return;

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchKPIs = async () => {
      setKpisLoading(true);
      setKpisError(null);

      try {
        logger.info(`Fetching KPIs data for company ${activeCompany.id}`);
        const response = await getKPIs(activeCompany.id, signal);

        if (signal.aborted) return;
        if (response && response.data) {
          setKpisData(response.data);
          logger.info('Loaded KPIs data');
        } else {
          setKpisData(null);
          logger.info('No KPIs data available');
        }
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching KPIs data:', err);
        setKpisError(err.message || t('kpis.error'));
      } finally {
        if (!signal.aborted) setKpisLoading(false);
      }
    };

    fetchKPIs();
    return () => ac.abort();
  }, [activeCompany?.id, t]);

  useEffect(() => {
    if (!userId || !companyId) return;
    const stored = readProjects(userId, companyId);
    setActiveTab(stored.activeTab);
    setSortColumn(stored.sortColumn);
    setSortDirection(stored.sortDirection);
    setIsChatDrawerOpen(readAgentOpen(userId));
    setHydratedFor(`${userId}:${companyId}`);
  }, [userId, companyId]);

  useEffect(() => {
    if (!userId || !companyId) return;
    if (hydratedFor !== `${userId}:${companyId}`) return;
    writeProjects(userId, companyId, { activeTab, sortColumn, sortDirection });
    writeAgentOpen(userId, isChatDrawerOpen);
  }, [hydratedFor, userId, companyId, activeTab, sortColumn, sortDirection, isChatDrawerOpen]);

  // Get current projects list based on active tab
  const getCurrentProjects = () => {
    return projects[activeTab] || [];
  };

  const handleProjectClick = (project) => {
    const projectId = project.project_id ?? project.id;
    navigate(pathWithPreservedParams(`/projects/${projectId}`, searchParams));
  };

  if (loading) {
    return (
      <div className="projects-view-container">
        <div className="projects-view">
          <div className="projects-loading">
            <div className="loading-spinner"></div>
            <p>{t('projects.loading')}</p>
          </div>
        </div>
        <ChatDrawer
          isOpen={isChatDrawerOpen}
          onToggle={() => setIsChatDrawerOpen(!isChatDrawerOpen)}
          context="projects"
          conversation={conversation}
        />
      </div>
    );
  }

  if (error && !projects.active.length && !projects.inactive.length && !projects.no_activity.length && !projects.closed.length && !projects.internal.length) {
    return (
      <div className="projects-view-container">
        <div className="projects-view">
          <div className="projects-error">
            <p className="error-message">⚠️ {error}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              {t('dashboards.retry')}
            </button>
          </div>
        </div>
        <ChatDrawer
          isOpen={isChatDrawerOpen}
          onToggle={() => setIsChatDrawerOpen(!isChatDrawerOpen)}
          context="projects"
          conversation={conversation}
        />
      </div>
    );
  }

  const currentProjects = getCurrentProjects();

  // Compute KPI cards content
  const renderKPIContent = () => {
    if (kpisLoading) {
      return (
        <div className="projects-kpis-loading">
          <div className="loading-spinner"></div>
          <p>{t('kpis.loading')}</p>
        </div>
      );
    }

    if (kpisError) {
      return (
        <div className="projects-kpis-error">
          <p className="error-message">⚠️ {kpisError}</p>
        </div>
      );
    }

    if (!kpisData) {
      return null;
    }

    // Check if any KPI has a value
    const hasAnyValue = kpisData.break_even_hourly_rate != null ||
                       kpisData.billable_rate != null;

    if (!hasAnyValue) {
      return null;
    }

    return (
      <div className="kpi-cards-horizontal">
        {kpisData.break_even_hourly_rate != null && (
          <Tippy
            content={t('kpis.breakEvenHourlyRateExplanation')}
            placement="top"
            interactive={false}
            delay={[200, 0]}
            maxWidth={300}
          >
            <div className="kpi-card highlight">
              <div className="kpi-label">{t('kpis.breakEvenHourlyRate')}</div>
              <div className="kpi-value">{formatCurrency(kpisData.break_even_hourly_rate)}</div>
            </div>
          </Tippy>
        )}

        {kpisData.billable_rate != null && (
          <Tippy
            content={t('kpis.billableRateExplanation')}
            placement="top"
            interactive={false}
            delay={[200, 0]}
            maxWidth={300}
          >
            <div className="kpi-card">
              <div className="kpi-label">{t('kpis.billableRate')}</div>
              <div className="kpi-value">{formatPercentage(kpisData.billable_rate)}</div>
            </div>
          </Tippy>
        )}
      </div>
    );
  };

  // Check if KPI section should be rendered
  const kpiContent = renderKPIContent();
  const shouldShowKPISection = kpiContent !== null;

  return (
    <div className="projects-view-container">
      <div className="projects-view">
        <div className="projects-header">
          <h1 className="projects-title">{t('projects.title')}</h1>
        </div>
        <div className="projects-tabs-row">
          <ProjectsTabs activeTab={activeTab} onTabChange={setActiveTab} />
          {/* KPI Cards */}
          {shouldShowKPISection && (
            <div className="projects-kpi-section">
              {kpiContent}
            </div>
          )}
        </div>

        <div className="projects-content-wrapper">
          <div className="projects-list-wrapper">
            <div className="projects-table-scroll-container">
              <ProjectsList
                projects={currentProjects}
                onProjectClick={handleProjectClick}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSortChange={(column, direction) => {
                  setSortColumn(column);
                  setSortDirection(direction);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <ChatDrawer
        isOpen={isChatDrawerOpen}
        onToggle={() => setIsChatDrawerOpen(!isChatDrawerOpen)}
        context="projects"
        conversation={conversation}
      />
    </div>
  );
};

export default ProjectsView;
